'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useDebouncedCallback } from 'use-debounce';
import { mutate } from 'swr';

import axiosInstance from '@/lib/axios';
import { toast } from 'sonner';
import { Form } from '@/components/ui/form';
import { DynamicFormRenderer } from '@/components/forms/dynamic-form-renderer';
import { buildDynamicZodSchema, getDefaultValueForField } from '@/lib/dynamic-form-schema';
import type {
  FormField as DynamicFormField,
  OnboardingFormConfig,
  FormAnswerValue,
  OnboardingAnswerEntry,
} from '@/types';
import {
  buildRichOnboardingAnswers,
  getFormValueFromStoredOnboarding,
  normalizeOnboardingDateStorageValue,
  sanitizeOnboardingAnswers,
} from '@/lib/onboarding-answers';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

/** Stable fallback — default param `{}` would be a new object every render. */
const EMPTY_ONBOARDING_INITIAL: Record<string, FormAnswerValue | OnboardingAnswerEntry> = {};

interface OnboardingMembershipAutosaveFormProps {
  communityId: string;
  communityName: string;
  config: OnboardingFormConfig;
  initialAnswers?: Record<string, FormAnswerValue | OnboardingAnswerEntry>;
}

export function OnboardingMembershipAutosaveForm({
  communityId,
  communityName,
  config,
  initialAnswers,
}: OnboardingMembershipAutosaveFormProps) {
  const resolvedInitial = initialAnswers ?? EMPTY_ONBOARDING_INITIAL;
  const allFields = useMemo<DynamicFormField[]>(
    () => (config.steps || []).flatMap((s) => (Array.isArray(s.fields) ? s.fields : [])),
    [config.steps]
  );
  const schema = useMemo(() => buildDynamicZodSchema(allFields), [allFields]);
  const defaults = useMemo(
    () =>
      Object.fromEntries(
        allFields.map((f) => {
          const provided = getFormValueFromStoredOnboarding(resolvedInitial[f.id]);
          if (provided !== undefined && provided !== null) {
            if (f.type === 'DATE' && typeof provided === 'string') {
              return [f.id, normalizeOnboardingDateStorageValue(provided)];
            }
            return [f.id, provided];
          }
          return [f.id, getDefaultValueForField(f)];
        })
      ),
    [allFields, resolvedInitial]
  );

  const form = useForm<Record<string, FormAnswerValue>>({
    resolver: zodResolver(schema) as unknown as Resolver<Record<string, FormAnswerValue>>,
    defaultValues: defaults,
    mode: 'onChange',
  });

  const uploadingRef = useRef(false);
  const skipNextWatchRef = useRef(false);
  const [saving, setSaving] = useState(false);

  const persist = async (values: Record<string, FormAnswerValue>) => {
    if (uploadingRef.current) return;
    const sanitized = sanitizeOnboardingAnswers(values);
    try {
      setSaving(true);
      await axiosInstance.patch(`/api/communities/${communityId}/membership`, {
        onboardingAnswers: buildRichOnboardingAnswers(config, sanitized),
      });
      await mutate('/api/user');
      skipNextWatchRef.current = true;
      form.reset(values, { keepValues: true, keepDirty: false });
    } catch {
      toast.error('Could not save additional details');
    } finally {
      setSaving(false);
    }
  };

  const debouncedPersist = useDebouncedCallback(persist, 850);

  useEffect(() => {
    const sub = form.watch((_v) => {
      if (skipNextWatchRef.current) {
        skipNextWatchRef.current = false;
        return;
      }
      if (!form.formState.isDirty) return;
      if (uploadingRef.current) return;
      debouncedPersist(form.getValues());
    });
    return () => sub.unsubscribe();
  }, [form, debouncedPersist]);

  if (allFields.length === 0) {
    return null;
  }

  return (
    <Card
      className={cn(
        'w-full rounded-2xl border-0 md:rounded-3xl md:border md:border-border',
        'bg-background/50 backdrop-blur-md shadow-none md:shadow-xl overflow-hidden'
      )}
    >
      <CardHeader className="flex flex-col gap-2 p-1 pb-3 sm:flex-row sm:items-start sm:justify-between sm:gap-3 sm:p-6 sm:pb-2">
        <div className="min-w-0 flex-1">
          <CardTitle className="text-base font-bold leading-snug md:text-lg">
            {communityName}
          </CardTitle>
          <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground md:text-xs">
            Updates save automatically shortly after you make changes.
          </p>
        </div>
        {saving && (
          <span className="text-[11px] font-semibold text-primary inline-flex items-center gap-1 shrink-0 self-start sm:self-auto md:text-xs">
            <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> Saving…
          </span>
        )}
      </CardHeader>
      <CardContent className="space-y-6 px-0 pb-4 pt-0 sm:space-y-7 md:space-y-8 md:p-6 md:pt-0">
        <Form {...form}>
          {(config.steps || []).map((step, si) =>
            step.fields?.length ? (
              <section key={si} className="space-y-3 md:space-y-4">
                <div className="flex items-center gap-2 pb-1.5 border-b border-border/35 md:pb-2 md:border-border/40">
                  <h3 className="font-bold text-[13px] tracking-tight text-foreground md:text-sm">
                    {step.title}
                  </h3>
                </div>
                <DynamicFormRenderer
                  fields={step.fields}
                  control={form.control}
                  uploadFolder="onboarding-answers"
                  onUploadingChange={(uploading) => {
                    uploadingRef.current = uploading;
                    if (
                      !uploading &&
                      form.formState.isDirty &&
                      Object.keys(form.formState.dirtyFields).length > 0
                    ) {
                      debouncedPersist(form.getValues());
                    }
                  }}
                />
              </section>
            ) : null
          )}
        </Form>
      </CardContent>
    </Card>
  );
}
